import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, DestroyRef, inject, ChangeDetectorRef } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import * as moment from 'moment';
import { TextareaComponent } from "src/app/shared/components/inputs/simple/textarea/textarea.component";
import { TimeComponent } from "src/app/shared/components/inputs/simple/time/time.component";
import { SelectComponent } from "src/app/shared/components/inputs/simple/select/select.component";
import { PipesModule } from "../../../../../../shared/pipes/pipe-module";
import { IconComponent } from "src/app/shared/components/icon/icon.component";
import { debounceTime, startWith, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { validarHora, computeNewPosition } from './validators/validator';
import { AccionAgregada, IntervalView, MarksSelected } from '../../interface/interfaces';
import { MatCheckboxModule } from "@angular/material/checkbox";
import { CommonModule } from '@angular/common';
import { ConfigurationsService } from 'src/app/shared/services/configurations.service';
import { REFRESH_INDEXED } from 'src/app/core/indexedDB/helpers/enums.enums';
import { configurations } from 'src/app/shared/interfaces/configurations.interface';
import { REGUL_REASONS_WITH_EXPLANATION } from '../../helpers/enums';

@Component({
  standalone: true,
  selector: 'mg-action-form',
  templateUrl: './action-form.component.html',
  styleUrls: ['./action-form.component.scss'],
  imports: [
    TextareaComponent,
    TimeComponent,
    SelectComponent,
    PipesModule,
    IconComponent,
    ReactiveFormsModule,
    MatCheckboxModule,
    CommonModule,
  ]
})
export class ActionFormComponent implements OnInit, OnChanges {
  @Input({ required: true }) markSelected!: MarksSelected;
  @Input() esEliminar: boolean = false;
  @Input() actionDate!: moment.Moment;
  @Input() selectedDate!: Date;
  @Input({ required: true }) accionForm!: FormGroup;
  @Input() intervals: IntervalView[] = [];
  @Input() adjacentIntervals: IntervalView[] = [];
  @Input() acciones: AccionAgregada[] = [];

  @Output() cancelAction = new EventEmitter<void>();
  @Output() saveAction = new EventEmitter<boolean>();
  @Output() markDateChanged = new EventEmitter<moment.Moment>();

  horaError: string = '';
  dynamicLabel: string = '';
  isNextDay: boolean = false;
  reasonOptions: configurations[] = [];
  requiresExplanation: boolean = false;
  reasonCodeControl = new FormControl<string>('', Validators.required);
  explicacionControl = new FormControl<string>('');
  explicacionMaxLength: number = 550;
  private static readonly SREASON_MAX_LENGTH = 550;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _configurationsService = inject(ConfigurationsService);

  get isToday(): boolean {
    const now = new Date();
    const selected = new Date(this.selectedDate);
    return selected.toDateString() === now.toDateString();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.accionForm || !this.actionDate) return;
    if (changes['adjacentIntervals'] || changes['intervals'] || changes['acciones']) {
      this.ejecutarValidacion(this.accionForm.get('sHour')?.value, this.actionDate);
    }
  }

  ngOnInit() {
    this.isNextDay = this.markSelected?.isNextDay || false;
    this.loadReasonOptions();
    this.listenReasonCodeChanges();
    this.accionForm.valueChanges
      .pipe(
        startWith(this.accionForm.value),
        debounceTime(100),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((val) => {
        this.ejecutarValidacion(val.sHour, this.actionDate);
      });
  }

  private loadReasonOptions(): void {
    this._configurationsService.getConfig(REFRESH_INDEXED.EVERY_DAY, ['REGUL_MARKS'])
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((resp: any) => {
        this.reasonOptions = resp?.['REGUL_MARKS'] ?? [];
        this.cdr.detectChanges();
      });
  }

  private listenReasonCodeChanges(): void {
    this.reasonCodeControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((code: string | null) => {
        const option = this.reasonOptions.find(o => o.sCodigo === code);
        const desc = option?.sDescripcion ?? '';
        this.requiresExplanation = !!code && REGUL_REASONS_WITH_EXPLANATION.has(String(code));
        if (this.requiresExplanation) {
          this.explicacionControl.setValidators([
            Validators.required,
            Validators.minLength(15),
            Validators.maxLength(550),
          ]);
        } else {
          this.explicacionControl.clearValidators();
          this.explicacionControl.setValue('', { emitEvent: false });
        }
        this.explicacionControl.updateValueAndValidity({ emitEvent: false });
        this.accionForm.get('sReason')?.setValue(this.composeReason(desc, this.explicacionControl.value ?? ''));
        this.cdr.detectChanges();
      });

    this.explicacionControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((expl) => {
        const sanitized = (expl ?? '').replace(/[^A-Za-zÁÉÍÓÚÑáéíóúñÄËÏÖÜäëïöü0-9 ]/g, '');
        if (sanitized !== expl) {
          this.explicacionControl.setValue(sanitized, { emitEvent: false });
        }
        const code = this.reasonCodeControl.value;
        const option = this.reasonOptions.find(o => o.sCodigo === code);
        const desc = option?.sDescripcion ?? '';
        this.accionForm.get('sReason')?.setValue(this.composeReason(desc, sanitized));
      });
  }

  private composeReason(desc: string, explicacion: string): string {
    const expl = (explicacion ?? '').trim();
    if (this.requiresExplanation && expl) return `${desc}: ${expl}`;
    return desc;
  }

  onNextDayToggle(checked: boolean) {
    this.isNextDay = checked;
    const currentHour = this.accionForm.get('sHour')?.value;
    this.ejecutarValidacion(currentHour, this.actionDate);
  }

  ejecutarValidacion(hour: string, date: moment.Moment) {
    if (!hour || this.esEliminar) {
      this.horaError = '';
      this.dynamicLabel = '';
    } else {
      this.horaError = validarHora(
        hour,
        this.markSelected,
        date.toDate(),
        this.intervals,
        this.acciones,
        this.isNextDay,
        this.adjacentIntervals
      );
      this.dynamicLabel = this.buildDynamicLabel(hour, date, this.isNextDay);
    }
    this.cdr.detectChanges();
  }

  private buildDynamicLabel(sHour: string, date: moment.Moment, isNextDay: boolean): string {
    const pos = computeNewPosition(sHour, date, isNextDay, this.markSelected, this.intervals, this.acciones);
    if (!pos) return '';
    const isEdit = !!this.markSelected.sOriginalHour;
    const bold = (h: string) => `<span class="font-bold">${h}</span>`;
    const intervalPart = `Intervalo ${pos.intervalNum}`;
    if (isEdit) {
      return `${intervalPart} · Edición de ${pos.type} de ${bold(this.markSelected.sOriginalHour!)} a ${bold(sHour)}`;
    }
    return `${intervalPart} · Registro de ${pos.type} a las ${bold(sHour)}`;
  }

  onCancel() {
    this.cancelAction.emit();
  }

  onSave() {
    const currentHour = this.accionForm.get('sHour')?.value;
    this.ejecutarValidacion(currentHour, this.actionDate);
    if (this.horaError) return;
    const isInvalid = this.reasonCodeControl.invalid || (this.requiresExplanation && this.explicacionControl.invalid);

    if (isInvalid) {
      this.reasonCodeControl.markAsTouched();
      this.explicacionControl.markAsTouched();
    } else {
      this.saveAction.emit(this.isNextDay);
    }
  }

  onDateChanged(newDate: moment.Moment) {
    this.markDateChanged.emit(newDate);
    const currentHour = this.accionForm.get('sHour')?.value;
    this.ejecutarValidacion(currentHour, newDate);
  }
}